import { HStack, Td, Tr } from '@chakra-ui/react';
import { useToast } from 'utils/hooks';
import { Input, Button, Tag, IconButton, TruncatableText, Tooltip } from 'Atoms';
import { useEffect, useMemo, useState } from 'react';
import { Typography } from 'Tokens';
import { CompanyIcon } from 'Tokens/Icons/Data';
import { ArrowUpRightIcon } from 'Tokens/Icons/Direction';
import { RefreshIcon } from 'Tokens/Icons/Function';
import {
  Esrs_EsrsAssessmentCompanyIntegrationRu_Constraint_,
  Esrs_EsrsAssessmentCompanyIntegrationRu_Update_Column_,
  SingleAssessmentCompanyIntegrationRuDocument_,
  SingleAssessmentCompanyIntegrationRuQuery_,
  SingleAssessmentCompanyIntegrationRuQueryVariables_,
  useUpsertAssessmentCompanyIntegrationMutation,
  useUpsertAssessmentCompanyIntegrationRuMutation,
} from 'models';
import { MoreScopeBusinessUnitRow } from '../MoreScopeBusinessUnits/MoreScopeBusinessUnits';
import { SavingState } from '../MoreScopeCompany/MoreScopeCompany';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'utils/translation';
import { timeSinceDate } from 'utils/date';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';

type ReportingUnitDetails =
  | {
      esrsReportingUnitId: any;
      syncedAt?: any | null;
      apiKey?: string | null;
      esrsAssessmentCompanyIntegrationId: any;
    }
  | undefined;

export const MoreScopeBusinessUnitsRow = ({
  subRow,
  assessmentYears,
  display,
  userId,
}: {
  subRow: MoreScopeBusinessUnitRow;
  assessmentYears?: number[];
  display: string;
  userId?: string;
}) => {
  const toast = useToast();
  const translations = useTranslation('common');
  const [upsertAssessmentCompanyIntegration] = useUpsertAssessmentCompanyIntegrationMutation();
  const [upsertAssessmentCompanyIntegrationRU] = useUpsertAssessmentCompanyIntegrationRuMutation();
  const { companyId, framework = '' } = useParams();

  const [reportingUnitDetails, setReportingUnitDetails] = useState<ReportingUnitDetails>();
  const [moreScopeYears, setMoreScopeYears] = useState<number[]>([]);
  const [api, setApi] = useState('');
  const [savingState, setSavingState] = useState(SavingState.save);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncedTime, setSyncedTime] = useState('');
  const [shouldfetchMoreScopeYears, setShouldFetchMoreScopeYears] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const nhost = useNhostClient();

  const fetchMoreScopeYears = async () => {
    if (!assessmentYears) return;

    setLoading(true);
    try {
      const res = await nhost.functions.post('/esrs/get-morescope-years', {
        apiKey: api,
        years: assessmentYears,
      });

      setMoreScopeYears((res.body as { result: number[] })?.result);
    } catch (error) {
      setMoreScopeYears([]);
      console.error(error);
    }
    setLoading(false);
    setShouldFetchMoreScopeYears(false);
  };

  const isSyncDisabled = useMemo(() => {
    return !moreScopeYears.includes(subRow.reportingYear);
  }, [moreScopeYears]);

  const onSave = async () => {
    if (savingState === SavingState.save) {
      try {
        setSavingState(SavingState.saving);

        await upsertAssessmentCompanyIntegration({
          variables: {
            object: {
              esrsAssessmentId: subRow?.assessmentId,
              companyIntegrationId: subRow?.companyIntegrationId,
              esrsAssessmentCompanyIntegrationRUs: {
                data: [
                  {
                    esrsReportingUnitId: subRow?.id,
                    apiKey: api,
                  },
                ],
                on_conflict: {
                  constraint:
                    Esrs_EsrsAssessmentCompanyIntegrationRu_Constraint_.EsrsAssessmentCompanyIntegrationRuEsrsReportingUnitIdEsrsAsse_,
                  update_columns: [Esrs_EsrsAssessmentCompanyIntegrationRu_Update_Column_.ApiKey_],
                },
              },
            },
          },
          refetchQueries: [],
          onCompleted: async (result) => {
            const integrationResult = await nhost.graphql.request<
              SingleAssessmentCompanyIntegrationRuQuery_,
              SingleAssessmentCompanyIntegrationRuQueryVariables_
            >(SingleAssessmentCompanyIntegrationRuDocument_, {
              reportingUnitId: subRow?.id,
              assessmentCompanyIntegrationId:
                result.insert_esrs_EsrsAssessmentCompanyIntegration_one?.id,
            });
            const reportingUnitData =
              integrationResult.body.data?.esrs_EsrsAssessmentCompanyIntegrationRU?.[0];
            setReportingUnitDetails({
              esrsReportingUnitId: subRow?.id,
              apiKey: reportingUnitData?.apiKey,
              syncedAt: reportingUnitData?.syncedAt,
              esrsAssessmentCompanyIntegrationId:
                result.insert_esrs_EsrsAssessmentCompanyIntegration_one?.id,
            });
            toast({ text: 'API key saved' });
            setSavingState(SavingState.saved);
            setShouldFetchMoreScopeYears(true);
          },
        });
      } catch {
        setSavingState(SavingState.save);
        toast({ text: 'Failed to save Api Key', variant: 'danger' });
      }
    }
  };

  const onSync = async (values: {
    assessmentId: string;
    year: number;
    projectLeaderId: string;
    companyReportingUnitId: string;
    apiKey: string;
    assessmentCompanyIntegrationId: string;
  }) => {
    if (!isSyncing) {
      setIsSyncing(true);
      const {
        assessmentId,
        year,
        projectLeaderId,
        companyReportingUnitId,
        apiKey,
        assessmentCompanyIntegrationId,
      } = values;
      try {
        const res = await nhost.functions.post('/esrs/integrate-morescope-data', {
          assessmentId: assessmentId,
          assessmentYear: year,
          companyReportingUnitId: companyReportingUnitId,
          userId: userId ?? '',
          assessmentProjectLeaderId: projectLeaderId,
          apiKey,
        });

        await upsertAssessmentCompanyIntegrationRU({
          variables: {
            object: {
              esrsReportingUnitId: companyReportingUnitId,
              esrsAssessmentCompanyIntegrationId: assessmentCompanyIntegrationId,
              syncedAt: new Date(),
            },
          },
          refetchQueries: [],
          onError: () => {
            toast({
              text: 'Error saving synced time',
              variant: 'danger',
            });
          },
          onCompleted: async () => {
            const result = await nhost.graphql.request<
              SingleAssessmentCompanyIntegrationRuQuery_,
              SingleAssessmentCompanyIntegrationRuQueryVariables_
            >(SingleAssessmentCompanyIntegrationRuDocument_, {
              reportingUnitId: subRow?.id,
              assessmentCompanyIntegrationId,
            });
            const reportingUnitData =
              result.body?.data?.esrs_EsrsAssessmentCompanyIntegrationRU?.[0];
            setReportingUnitDetails({
              esrsReportingUnitId: subRow?.id,
              apiKey: reportingUnitData?.apiKey,
              syncedAt: reportingUnitData?.syncedAt,
              esrsAssessmentCompanyIntegrationId: assessmentCompanyIntegrationId,
            });
            setSyncedTime(reportingUnitData?.syncedAt);
          },
        });

        toast({
          text: 'Data integrated successfully',
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage === 'E1-6 not assessed') {
          toast({
            text: 'Please assess the disclosure requirement E1-6 in Climate Change first.',
            variant: 'danger',
            closable: true,
            duration: null,
          });
        } else {
          console.error(error);
          toast({
            variant: 'danger',
            text: 'Unable to create integrate data. Please review API Key',
          });
        }
      }
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    const company = subRow?.companyIntegrationRU?.find(
      (company) => company?.esrsReportingUnitId === subRow?.id
    );
    if (company) {
      setReportingUnitDetails(company);
      setApi(company?.apiKey ?? '');
      setSyncedTime(company.syncedAt);
      if (company?.apiKey) {
        setSavingState(SavingState.saved);
        setShouldFetchMoreScopeYears(true);
      }
    }
  }, []);

  useEffect(() => {
    if (shouldfetchMoreScopeYears) {
      fetchMoreScopeYears();
    }
  }, [shouldfetchMoreScopeYears]);

  return (
    <Tr
      display={display}
      background={'bg.unknown.muted'}
      key={'header'}
      borderTop={'1px solid'}
      borderColor={'border.decorative'}
      alignItems={'center'}
      height={'50px'}
    >
      <Td pt={'12px'} pb={'12px'} pl={'44px'} pr={'0px'} borderBottom={'none'} width={'225px'}>
        <HStack>
          <CompanyIcon />
          <TruncatableText text={subRow?.name} fontSize={'14px'} maxWidth={'157px'} pr={'40px'} />
        </HStack>
      </Td>
      <Td padding={'0'} borderBottom={'none'} width={'287px'}>
        <HStack alignSelf={'flex-start'}>
          <Input
            value={api}
            type="password"
            variant="ghost"
            onChange={(e) => {
              setApi(e.target.value);
              if (savingState != SavingState.save) {
                setSavingState(SavingState.save);
              }
              if (savingState === SavingState.save) {
                if (e.target.value === reportingUnitDetails?.apiKey) {
                  setSavingState(SavingState.saved);
                  setShouldFetchMoreScopeYears(false);
                }
              }
            }}
            placeholder="Enter API key"
            isDisabled={isSyncing || savingState === SavingState.saving}
            paddingRight={'70px'}
            width={'287px'}
          />
          {!!api && (
            <Button
              variant={'ghost'}
              isLoading={savingState === SavingState.saving}
              onClick={() => onSave()}
              minW={'fit-content'}
              height={'28px'}
              isDisabled={isSyncing}
              marginLeft={
                savingState === SavingState.saved
                  ? '-68px'
                  : savingState === SavingState.saving
                    ? '-80px'
                    : '-60px'
              }
            >
              <Typography color={'text.selected'} fontWeight={'500'} lineHeight={'21px'}>
                {savingState === SavingState.save || savingState === SavingState.saving
                  ? 'Save'
                  : 'Saved'}
              </Typography>
            </Button>
          )}
        </HStack>
      </Td>
      <Td padding={'0'} borderBottom={'none'} width={'144px'} pl={'24px'}>
        <HStack>
          <Tooltip
            label={'No matching data found in MoreScope'}
            isDisabled={!isSyncDisabled || savingState !== SavingState.saved}
          >
            <Button
              height={'28px'}
              leftIcon={
                <RefreshIcon
                  color={
                    savingState === SavingState.saved && !loading && !isSyncDisabled
                      ? 'text.action'
                      : 'text.disabled'
                  }
                />
              }
              justifyContent="flex-start"
              variant={'secondary'}
              onClick={() =>
                onSync({
                  assessmentId: subRow?.assessmentId,
                  apiKey: reportingUnitDetails?.apiKey ?? '',
                  projectLeaderId: subRow?.projectLeaderId,
                  year: subRow?.reportingYear,
                  companyReportingUnitId: subRow?.id,
                  assessmentCompanyIntegrationId:
                    reportingUnitDetails?.esrsAssessmentCompanyIntegrationId ?? '',
                })
              }
              padding={'6px 8px'}
              isDisabled={savingState !== SavingState.saved || isSyncDisabled || loading}
              isLoading={isSyncing || loading}
            >
              <Typography
                color={
                  savingState === SavingState.saved && !loading && !isSyncDisabled
                    ? 'text.action'
                    : 'text.disabled'
                }
              >
                Sync data
              </Typography>
            </Button>
          </Tooltip>
        </HStack>
      </Td>
      <Td padding={'0'} borderBottom={'none'} width={'150px'} pl={'24px'}>
        <HStack>
          <Tag variant={!!syncedTime ? 'info' : 'undefined'}>
            {!!syncedTime ? (
              <HStack>
                <Typography variant="bodyStrong" color={'text.info'}>
                  Synced
                </Typography>
                <TruncatableText
                  color="text.info"
                  fontSize={'12px'}
                  text={`${timeSinceDate(new Date(syncedTime), translations)}`}
                />
              </HStack>
            ) : (
              <Typography>Manual Input</Typography>
            )}
          </Tag>
        </HStack>
      </Td>
      <Td padding={'0'} borderBottom={'none'} width={'48px'} pl={'20px'}>
        <HStack>
          <IconButton
            icon={
              <Link
                to={`/${companyId}/sustainability-reporting/${framework}/${subRow?.assessmentId}`}
                target="_blank"
              >
                <ArrowUpRightIcon mt={'5px'} />
              </Link>
            }
            aria-label="chevron icon"
            variant={'ghost'}
          />
        </HStack>
      </Td>
    </Tr>
  );
};
